import {
  AsyncSelect,
  FormatOptionLabelMeta,
  StylesConfig,
} from '@databases/atlaskit/Select';
import { flaggingCallback } from '@databases/banners';
import { authenticateJira } from '@databases/connections/jira-auth';
import JiraIssueLink, {
  JiraIssueIcon,
  JiraIssueLabel,
  JiraIssueStatus,
} from '@databases/content-link/JiraIssueLink';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { CloudId, JiraIssueRef, JiraServerId } from '@databases/types/confluence';
import { JiraAuthenticationError } from '@databases/utils/confluence/jira';
import { searchJiraIssues } from '@databases/utils/redux/jira/actions';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const OptionContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const getOptionValue = (value: JiraIssueRef) => value.key;

const getOptionLabel = (value: JiraIssueRef) => value.key;

const styles: StylesConfig<JiraIssueRef> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
  singleValue: (base) => ({
    ...base,
    padding: 2, // Give space to preview box shadow
    lineHeight: '20px',
    pointerEvents: 'none', // Prevent interaction with preview
  }),
};

export interface JiraIssuePickerProps {
  cloudId: CloudId;
  serverId: JiraServerId;
  value: JiraIssueRef | undefined;
  onChange: (value: JiraIssueRef | undefined) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isClearable?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isSeamless?: boolean;
  isLinkPreview?: boolean;
}

const JiraIssuePicker: FunctionComponent<JiraIssuePickerProps> = ({
  cloudId,
  serverId,
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isClearable,
  isInvalid,
  isDisabled,
  isSeamless,
  isLinkPreview,
}) => {
  const { formatMessage } = useIntl();
  const handleChange = useCallback(
    (val: JiraIssueRef | null) => {
      onChange(val ?? undefined);
    },
    [onChange]
  );

  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();

  const loadOptions = useCallback(
    async (inputValue: string) => {
      try {
        return await dispatch(searchJiraIssues(cloudId, serverId, inputValue));

        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (e) {
        if (e instanceof JiraAuthenticationError && e.authenticationPath) {
          const authPath = e.authenticationPath;

          let flagShown = false;

          if (flagShown) {
            return [];
          }

          currentSnackbarState.setMessage(
            formatMessage(messages['snackbar-error.jira.auth-required']),
            {
              id: 'jira-auth',
              type: 'error',
              showCloseButton: true,
              actions: [
                {
                  content: formatMessage(messages['snackbar-error.jira.authenticate']),
                  onClick: flaggingCallback(async () => {
                    await authenticateJira(
                      cloudId,
                      serverId,
                      authPath,
                      store,
                      formatMessage(messages['snackbar-error.enable-popup'])
                    );
                    flagShown = false;
                  }),
                },
              ],
              onDismiss: () => {
                flagShown = false;
              },
            }
          );
          flagShown = true;

          return [];
        } else {
          throw e;
        }
      }
    },
    [cloudId, dispatch, formatMessage, serverId, store]
  );

  const formatOptionLabel = useCallback(
    (value: JiraIssueRef, meta: FormatOptionLabelMeta<JiraIssueRef>) => {
      if (meta.context === 'menu') {
        return (
          <OptionContainer>
            <JiraIssueIcon issue={value} />
            <JiraIssueLabel issue={value} />
            <JiraIssueStatus issue={value} />
          </OptionContainer>
        );
      } else if (isLinkPreview) {
        return <JiraIssueLink issue={value} useSmartLink={false} />;
      } else {
        return <JiraIssueLabel issue={value} />;
      }
    },
    [isLinkPreview]
  );

  return (
    <AsyncSelect<JiraIssueRef>
      value={value ?? null}
      onChange={handleChange}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      getOptionLabel={getOptionLabel}
      loadOptions={loadOptions}
      getOptionValue={getOptionValue}
      formatOptionLabel={formatOptionLabel}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={formatMessage(messages['select-dropdown.placeholder'])}
      loadingMessage={() =>
        formatMessage(messages['field-settings.select-picker-loading-text'])
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      autoFocus={autoFocus}
      styles={styles}
      isSeamless={isSeamless}
      defaultMenuIsOpen={autoFocus && !value}
    />
  );
};

export default JiraIssuePicker;
